import * as THREE from "three";
import { MathUtils, Vector2, Vector3 } from "three";
import { GlassPanels } from "../../extras/sidepanels/GlassPanels";
import { ExtraType } from "../../../entities/LedExtras";
import { ZipScreen } from "../../extras/sidepanels/ZipScreen";
import { AdjustableParapet } from "../../extras/sidepanels/AdjustableParapet";
/**
 * A model consists of modules depending on the input width and projection
 */
export class ModelSkeleton {
    constructor(scene) {
        this.scene = scene;
        this.data = scene.formData;
        this.windows = new GlassPanels(this.scene);
        this.zipScreen = new ZipScreen(this.scene);
        this.parapet = new AdjustableParapet(this.scene);
        this.createModules();
    }
    /**
     * Creates a modules that make up a model.
     */
    createModules() {
        const yPos = 255;
        for (let z = 0; z < this.data.measuredModel.modulesZ; z++) {
            const zPos = z * (this.data.measuredModel.width - this.scene.formData.measuredModel.model.edgeWidth);
            this.createModule(new Vector3(0, yPos, zPos), z + 1, 0);
            this.scene.lightSource.createModuleLights(new THREE.Vector3(0, yPos, zPos));
            for (let x = 1; x < this.data.measuredModel.modulesX; x++) {
                this.createModule(new Vector3(x * this.data.measuredModel.projection, yPos, zPos), z + 1, x);
                this.scene.lightSource.createModuleLights(new THREE.Vector3(x * this.data.measuredModel.projection, yPos, zPos));
            }
        }
        this.createPillarsForWindows();
        this.createMiddlePillarsForExtras();
    }
    /** Return the pillars when there is wall only for the edges of the model. **/
    createPillarsForWindows() {
        const leftSide = this.data.left.wall;
        const rightSide = this.data.right.wall;
        const closeSide = this.data.close.wall;
        const farSide = this.data.far.wall;
        const moduleWidth = this.data.measuredModel.width;
        const moduleProjection = this.data.measuredModel.projection;
        const dim = this.scene.dimensions;
        const edgeW = this.scene.formData.measuredModel.model.edgeWidth;
        const railCenterQty = Math.ceil(moduleWidth / 450);
        const slopePercent = moduleWidth / railCenterQty / 25 - 1;
        const slope = slopePercent / 100 * moduleProjection;
        let height = 260;
        const rotated = this.data.measuredModel.rotated;
        if (this.data.measuredModel.model.id > 3) {
            height += slope + this.scene.formData.measuredModel.model.edgeHeight;
        }
        let position;
        if (closeSide === true) {
            if (this.returnExtra(this.data.left.extra)) {
                position = rotated === true ? new THREE.Vector3(dim.left.position.x, 130, dim.left.position.z + dim.left.length / 2 - edgeW / 2) : new THREE.Vector3(dim.left.position.x + dim.left.length / 2 - edgeW / 2, 130, dim.left.position.z);
                this.createEdge(position, new THREE.Vector3(edgeW, 260, edgeW));
            }
            if (this.returnExtra(this.data.right.extra)) {
                position = rotated === true ? new THREE.Vector3(dim.right.position.x, 130, dim.right.position.z + dim.left.length / 2 - edgeW / 2) : new THREE.Vector3(dim.right.position.x + dim.right.length / 2 - edgeW / 2, 130, dim.right.position.z);
                this.createEdge(position, new THREE.Vector3(edgeW, 260, edgeW));
            }
        }
        if (farSide === true) {
            if (this.returnExtra(this.data.left.extra)) {
                position = rotated === true ? new THREE.Vector3(this.scene.dimensions.left.position.x, height / 2, this.scene.dimensions.left.position.z - dim.left.length / 2 + edgeW / 2) : new THREE.Vector3(this.scene.dimensions.left.position.x - dim.left.length / 2 + edgeW / 2, height / 2, this.scene.dimensions.left.position.z);
                this.createEdge(position, new THREE.Vector3(edgeW, height, edgeW));
            }
            if (this.returnExtra(this.data.right.extra)) {
                position = rotated === true ? new THREE.Vector3(this.scene.dimensions.right.position.x, height / 2, this.scene.dimensions.right.position.z - dim.right.length / 2 + edgeW / 2) : new THREE.Vector3(this.scene.dimensions.right.position.x - dim.right.length / 2 + edgeW / 2, height / 2, this.scene.dimensions.right.position.z);
                this.createEdge(position, new THREE.Vector3(edgeW, height, edgeW));
            }
        }
        if (rightSide === true) {
            if (this.returnExtra(this.data.close.extra)) {
                position = rotated === true ? new THREE.Vector3(this.scene.dimensions.close.position.x + dim.close.length / 2 - edgeW / 2, 130, this.scene.dimensions.close.position.z) : new THREE.Vector3(this.scene.dimensions.close.position.x, 130, this.scene.dimensions.close.position.z - dim.close.length / 2 + edgeW / 2);
                this.createEdge(position, new THREE.Vector3(edgeW, 260, edgeW));
            }
            if (this.returnExtra(this.data.far.extra)) {
                position = rotated === true ? new THREE.Vector3(this.scene.dimensions.far.position.x + dim.far.length / 2 - edgeW / 2, 130, this.scene.dimensions.far.position.z) : new THREE.Vector3(this.scene.dimensions.far.position.x, 130, this.scene.dimensions.far.position.z - dim.far.length / 2 + edgeW / 2);
                this.createEdge(position, new THREE.Vector3(edgeW, 260, edgeW));
            }
        }
        if (leftSide === true) {
            if (this.returnExtra(this.data.close.extra)) {
                position = rotated === true ? new THREE.Vector3(this.scene.dimensions.close.position.x + dim.close.length / 2 - edgeW / 2, 130, this.scene.dimensions.close.position.z) : new THREE.Vector3(this.scene.dimensions.close.position.x, 130, this.scene.dimensions.close.position.z + dim.close.length / 2 - edgeW / 2);
                this.createEdge(position, new THREE.Vector3(edgeW, 260, edgeW));
            }
            if (this.returnExtra(this.data.far.extra)) {
                position = rotated === true ? new THREE.Vector3(this.scene.dimensions.far.position.x + dim.far.length / 2 - edgeW / 2, 130, this.scene.dimensions.far.position.z) : new THREE.Vector3(this.scene.dimensions.far.position.x, 130, this.scene.dimensions.far.position.z + dim.far.length / 2 - edgeW / 2);
                this.createEdge(position, new THREE.Vector3(edgeW, 260, edgeW));
            }
        }
    }
    // Creates a middle leg for the extras which requires additional legs
    createMiddlePillarsForExtras() {
        let projection = this.scene.formData.measuredModel.projection;
        let width = this.scene.formData.measuredModel.width;
        let pvcMultiplier = 1;
        let pvcProjMultiplier = 1;
        if (this.scene.formData.measuredModel.rotated === true) {
            projection = this.scene.formData.measuredModel.width;
            width = this.scene.formData.measuredModel.projection;
        }
        // In the PVCs one module can have multiple legs
        if (this.scene.formData.measuredModel.model.id > 3) {
            if (width > 451 && width <= 900) {
                width = width / 2;
                pvcMultiplier = 2;
            }
            else if (width > 900) {
                width = width / 3;
                pvcMultiplier = 3;
            }
            if (projection > 700) {
                width = width / 2;
                pvcProjMultiplier = 2;
            }
        }
        // LEFT
        if (projection > 400 && projection <= 700 && this.scene.getActualSide("LEFT").extra === ExtraType.PARAPET || projection > 400 && projection <= 700 && this.scene.getActualSide("LEFT").extra === ExtraType.PARAPET_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.left, "LEFT");
        else if (projection > 500 && projection <= 700 && this.scene.getActualSide("LEFT").extra === ExtraType.ZIPSCREEN || projection > 500 && projection <= 700 && this.scene.getActualSide("LEFT").extra === ExtraType.GLASS_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.left, "LEFT");
        if (projection > 800 && this.scene.getActualSide("LEFT").extra === ExtraType.PARAPET || projection > 800 && this.scene.getActualSide("LEFT").extra === ExtraType.PARAPET_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.left, "LEFT", pvcProjMultiplier);
        // RIGHT
        if (projection > 400 && projection <= 700 && this.scene.getActualSide("RIGHT").extra === ExtraType.PARAPET || projection > 400 && projection <= 700 && this.scene.getActualSide("RIGHT").extra === ExtraType.PARAPET_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.right, "RIGHT");
        else if (projection > 500 && projection <= 700 && this.scene.getActualSide("RIGHT").extra === ExtraType.ZIPSCREEN || projection > 500 && projection <= 700 && this.scene.getActualSide("RIGHT").extra === ExtraType.GLASS_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.right, "RIGHT");
        if (projection > 800 && this.scene.getActualSide("RIGHT").extra === ExtraType.PARAPET || projection > 800 && this.scene.getActualSide("RIGHT").extra === ExtraType.PARAPET_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.right, "RIGHT", pvcProjMultiplier);
        // CLOSE
        if (width > 400 && this.scene.getActualSide("CLOSE").extra === ExtraType.PARAPET || width > 400 && this.scene.getActualSide("CLOSE").extra === ExtraType.PARAPET_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.close, "CLOSE", pvcMultiplier);
        else if (width > 500 && this.scene.getActualSide("CLOSE").extra === ExtraType.ZIPSCREEN || width > 500 && this.scene.getActualSide("CLOSE").extra === ExtraType.GLASS_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.close, "CLOSE");
        // FAR
        if (width > 400 && this.scene.getActualSide("FAR").extra === ExtraType.PARAPET || width > 400 && this.scene.getActualSide("FAR").extra === ExtraType.PARAPET_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.far, "FAR", pvcMultiplier);
        else if (width > 500 && this.scene.getActualSide("FAR").extra === ExtraType.ZIPSCREEN || width > 500 && this.scene.getActualSide("FAR").extra === ExtraType.GLASS_ZIPSCREEN)
            this.createMiddlePillar(this.scene.dimensions.far, "FAR");
    }
    createMiddlePillar(dim, name, PVCmultiplier = 1) {
        const edgeW = this.scene.formData.measuredModel.model.edgeWidth;
        let width = dim.length - edgeW * 2 - edgeW * (dim.modules * PVCmultiplier - 1);
        const frameSize = width / (dim.modules * PVCmultiplier);
        let startingPos = dim.position.x - width / 2 + frameSize / 2 - edgeW / 2 * (dim.modules * PVCmultiplier - 1);
        let startingPosZ = dim.position.z - width / 2 + frameSize / 2 - edgeW / 2 * (dim.modules * PVCmultiplier - 1);
        let position;
        let rotation = dim.rotationY;
        position = new THREE.Vector3(startingPos, 350, dim.position.z);
        for (let i = 0; i < dim.modules * PVCmultiplier; i++) {
            let mainPos = startingPos + frameSize * i + edgeW * i;
            let mainPosZ = startingPosZ + frameSize * i + edgeW * i;
            if (rotation === 0) {
                position = new THREE.Vector3(mainPos, 130, dim.position.z);
            }
            else {
                position = new THREE.Vector3(dim.position.x, 130, mainPosZ);
            }
            this.createEdge(position, new THREE.Vector3(edgeW, 260, edgeW));
        }
    }
    returnExtra(side) {
        return side !== ExtraType.NONE;
    }
    createEdge(pos, size, rotateY = 0, rotateZ = 0) {
        const pillarTextureRepeat = new Vector2(size.x / 1024 * 24, size.y / 1024 * 24);
        const edgeMat = this.scene.materialPicker.getMaterial(pillarTextureRepeat);
        const cubeGeometry = new THREE.BoxGeometry(size.x, size.y, size.z);
        const cubeMesh = new THREE.Mesh(cubeGeometry, edgeMat);
        cubeMesh.castShadow = true;
        cubeMesh.position.copy(pos);
        cubeMesh.rotateY(MathUtils.degToRad(rotateY));
        cubeMesh.rotateZ(MathUtils.degToRad(rotateZ));
        this.scene.threeScene.add(cubeMesh);
    }
}
